import React, { useState } from 'react';
import { Item } from '../lib/schema';
import { formatPrice, calculateMargin, calculateMarginPercent } from '../lib/pricing';

interface ResultCardProps {
  item: Item;
  onUpdate: (item: Item) => void;
}

const ResultCard: React.FC<ResultCardProps> = ({ item, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    buyPrice: item.buyPrice,
    suggestedPrice: item.suggestedPrice || 0,
    qty: item.qty
  });

  const handleSave = () => {
    const updatedItem = {
      ...item,
      buyPrice: editData.buyPrice,
      suggestedPrice: editData.suggestedPrice,
      qty: editData.qty
    };
    onUpdate(updatedItem);
    setIsEditing(false);
  };

  const handleSell = () => {
    const sellPrice = prompt(`Enter sell price for ${item.name}:`, item.suggestedPrice?.toString() || '');
    if (sellPrice && !isNaN(parseFloat(sellPrice))) {
      const updatedItem = {
        ...item,
        sold: {
          sellPrice: parseFloat(sellPrice),
          sellDate: new Date().toISOString().split('T')[0]
        }
      };
      onUpdate(updatedItem);
    }
  };

  const margin = item.sold ? calculateMargin(item.buyPrice, item.sold.sellPrice) : 0;
  const marginPercent = item.sold ? calculateMarginPercent(item.buyPrice, item.sold.sellPrice) : 0;

  return (
    <div className="glass-effect p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-pokemon-purple/20 rounded-lg flex items-center justify-center">
            <span className="text-pokemon-purple font-bold text-lg">
              {item.name.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="text-white font-bold">{item.name}</h3>
            <p className="text-gray-400 text-sm">{item.set} • {item.condition}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          {!item.sold && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {isEditing && !item.sold ? (
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm block mb-1">Buy Price (SEK)</label>
            <input
              type="number"
              value={editData.buyPrice}
              onChange={(e) => setEditData({...editData, buyPrice: parseFloat(e.target.value) || 0})}
              className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pokemon-purple"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm block mb-1">Suggested Price (SEK)</label>
            <input
              type="number"
              value={editData.suggestedPrice}
              onChange={(e) => setEditData({...editData, suggestedPrice: parseFloat(e.target.value) || 0})}
              className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pokemon-purple"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm block mb-1">Quantity</label>
            <input
              type="number"
              value={editData.qty}
              onChange={(e) => setEditData({...editData, qty: parseInt(e.target.value) || 1})}
              className="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pokemon-purple"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm block">Buy Price</label>
              <p className="text-white font-medium">{formatPrice(item.buyPrice)}</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm block">Suggested Price</label>
              <p className="text-white font-medium">{formatPrice(item.suggestedPrice || 0)}</p>
            </div>
          </div>

          {item.sold ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-sm block">Sell Price</label>
                <p className="text-green-400 font-medium">{formatPrice(item.sold.sellPrice)}</p>
              </div>
              <div>
                <label className="text-gray-400 text-sm block">Profit</label>
                <p className={`font-medium ${margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatPrice(margin)} ({marginPercent.toFixed(1)}%)
                </p>
              </div>
            </div>
          ) : (
            <div className="pt-2">
              <button
                onClick={handleSell}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Mark as Sold
              </button>
            </div>
          )}

          <div className="pt-2 border-t border-gray-600">
            <div className="flex justify-between text-sm text-gray-400">
              <span>Quantity: {item.qty}</span>
              <span>Added: {new Date(item.buyDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultCard;
